import React from "react";
import CategoryTemplate from "@components/category/categoryTemplate";
import FreeQuotePopup from "@components/shared/freeQuotePopup";
import Compare from "@components/shared/compare";
import { GetStaticPropsContext } from "next";
import {
    GQLCategories,
    GQLComparisonMetaQuery,
    GQLDomains,
    GQLTextBlocksQuery,
} from "@gql/types/gql";
import {
    addStyleClassToIframe,
    addStyleClassToTable,
    getCategoriesDescription,
    getCategoriesKeyWords,
    titleDecoder,
} from "@lib/sharedUtils";
import { useQuery } from "@tanstack/react-query";
import { GQLOffers } from "@gql/types/gql";
import MetaTags from "@components/shared/metaTags";
import initialCall, { initialCallPaths } from "@helpers/initial-call";
import { PLACE_HOLDER_IMAGE_PATH } from "@lib/sharedUtils";
import Form from "@components/shared/form";
import { Session } from "pages/api/session";
import { useRouter } from "next/router";
import { ICustomDomain, TranslationsObject } from "@genericTypes/sharedTypes";
import { translate } from "@helpers/translationObjectFormatter";
import FormFusion from "@components/shared/formFusion";

const Pg = ({
    offerSlugs,
    categoryMainUrl,
}: {
    offerSlugs: string[];
    categoryMainUrl: string;
}) => {
    const { query } = useRouter();
    const id = query.id as string;
    const { data: offersToCompare } = useQuery<GQLOffers[], Error>([
        "offersToCompare",
        { offerSlugs },
    ]);
    const { data: categoryById } = useQuery<GQLCategories, Error>([
        "categoryById",
        id,
    ]);
    const { data: domainInfo } = useQuery<ICustomDomain, Error>({
        queryKey: ["domain"],
    });
    const { data: textBlocks } = useQuery<GQLTextBlocksQuery, Error>({
        queryKey: ["textBlocks"],
    });
    const { data: session } = useQuery<Session, Error>({
        queryKey: ["session"],
    });
    const { data: comparisonMeta } = useQuery<GQLComparisonMetaQuery, Error>({
        queryKey: ["comparisonMeta"],
    });
    const { data: translations } = useQuery<TranslationsObject>([
        "translations",
    ]);
    const { data: formFusionForm } = useQuery({
        queryKey: ["formFusionForm"],
    });

    let pageTitle = undefined;
    if (offersToCompare?.length) {
        pageTitle = offersToCompare[0].name as string;
        for (let i = 1; i < offersToCompare?.length; i++) {
            if (i <= 3) {
                pageTitle += " vs " + (offersToCompare[i].name as string);
            } else {
                pageTitle += " vs More...";
                break;
            }
        }
    }

    return (
        <>
            {/* title should be vendor vs vendor Intego vs TotalAV | Several.com */}
            <MetaTags
                title={`${
                    comparisonMeta?.url?.title
                        ? comparisonMeta?.url?.title
                        : pageTitle
                        ? pageTitle
                        : (categoryById?.label as string)
                        ? (categoryById?.label as string)
                        : (categoryById?.name as string)
                } | Several.com`}
                image={
                    (categoryById?.mainImage?.fullPath as string) ??
                    PLACE_HOLDER_IMAGE_PATH
                }
                url={`${categoryMainUrl}`}
                description={
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    (comparisonMeta?.url?.meta?.description as string) ??
                    (getCategoriesDescription(
                        categoryById as GQLCategories,
                        domainInfo?.domain as GQLDomains,
                        translate(
                            translations,
                            "several_general_meta_description",
                        ),
                    ) as string)
                }
                keyWords={
                    getCategoriesKeyWords(
                        categoryById as GQLCategories,
                        domainInfo?.domain as GQLDomains,
                    ) as string
                }
            />
            <CategoryTemplate>
                <Compare
                    offersToCompare={offersToCompare}
                    pageTitle={pageTitle as string}
                />
                {formFusionForm ? (
                    <FormFusion category={categoryById} />
                ) : (
                    <Form shouldSplit={true} />
                )}
                {textBlocks &&
                    textBlocks.allTextBlocks &&
                    textBlocks.allTextBlocks.length > 0 && (
                        <div
                            className="postContainer"
                            itemProp="text"
                            style={{ marginBottom: "2rem" }}
                        >
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: addStyleClassToTable(
                                        addStyleClassToIframe(
                                            titleDecoder(
                                                textBlocks?.allTextBlocks[0]
                                                    ?.text as string,
                                                {
                                                    category:
                                                        categoryById?.name as string,
                                                    region: session
                                                        ?.visitDetails?.region,
                                                },
                                            ),
                                        ),
                                    ),
                                }}
                            />
                        </div>
                    )}
                <FreeQuotePopup category={categoryById as GQLCategories} />
            </CategoryTemplate>
        </>
    );
};

export const getStaticProps = async (context: GetStaticPropsContext) => {
    return await initialCall("CategoryByIdCompareSlug", context);
};
export function getStaticPaths() {
    return initialCallPaths();
}
export default Pg;
